import React from 'react';
import { Edit3, Check, AlertTriangle, X, Circle } from 'lucide-react';
import { getMatchStatusColors, getMatchStatusFromRow, getDisplayMatchStatus, getDisplayStatusColors } from '../services/colorService';
import './ResultsTile.css';

const ResultsTile = ({ fieldName, data, onEdit, editingDisabled }) => {
  // Handle null/undefined data prop
  if (!data) {
    return (
      <div className="results-tile" data-cy="result-tile">
        <div className="tile-header">
          <h3 className="field-name">{fieldName}</h3>
        </div>
        <div className="tile-content">
          <div className="value-row value-only">
            <span className="value-text">No data available</span>
          </div>
        </div>
      </div>
    );
  }
  // Use centralized color service
  const getMatchStatusColor = (matchLevel) => {
    return getMatchStatusColors(matchLevel).backgroundColor;
  };

  const getMatchStatusIcon = (matchLevel) => {
    switch (matchLevel) {
      case 'HIGH':
        return <Check size={12} />;
      case 'MEDIUM':
        return <AlertTriangle size={12} />;
      case 'LOW':
        return <AlertTriangle size={12} />;
      case 'NO_MATCH':
        return <X size={12} />;
      default:
        return <Circle size={12} />;
    }
  };

  // Use centralized color service
  const getSystemMatchColor = (systemMatch) => {
    return getMatchStatusColors(systemMatch).backgroundColor;
  };

  // Match the exact FieldModal styling with more intense colors
  const getMatchStatusStyles = (status) => {
    switch (status) {
      case 'perfect':
      case 'matched':
        return {
          background: '#bbf7d0', // More intense green (between pastel and primary)
          borderColor: '#22c55e',
          color: '#16a34a'
        };
      case 'maybe':
        return {
          background: '#fed7aa', // More intense orange (between pastel and primary)
          borderColor: '#f59e0b',
          color: '#d97706'
        };
      case 'mismatch':
        return {
          background: '#fecaca', // More intense red (between pastel and primary)
          borderColor: '#ef4444',
          color: '#dc2626'
        };
      case 'no_data':
        return {
          background: '#d1fae5', // Light green background
          borderColor: '#6ee7b7',
          color: '#047857'
        };
      default:
        return {
          background: '#d1d5db', // More intense gray (between pastel and primary)
          borderColor: '#9ca3af',
          color: '#6b7280'
        };
    }
  };

  const getMatchStatusOverrideIcon = (status, size = 16) => {
    switch (status) {
      case 'perfect':
      case 'matched':
        return <Check size={size} />;
      case 'maybe':
        return <AlertTriangle size={size} />;
      case 'mismatch':
        return <X size={size} />;
      case 'no_data':
        return <Circle size={size} />;
      default:
        return <Circle size={size} />;
    }
  };

  const formatValue = (value) => {
    if (value === 'Not Found' || value === null || value === undefined) {
      return 'Not Found';
    }
    return value;
  };

  // Use centralized match status logic (consistent with Case List)
  const getActualMatchStatus = () => {
    return getDisplayMatchStatus(data);
  };

  // Discrepancy detection disabled - header/footer colors already show match status
  const hasDiscrepancy = false;
  
  const isOverridden = data?.overridden;

  return (
    <div 
      className={`results-tile ${hasDiscrepancy ? 'has-discrepancy' : ''}`} 
      data-cy="result-tile"
    >
      {/* Header */}
      <div 
        className="tile-header"
        style={{ 
          backgroundColor: (() => {
            const status = getActualMatchStatus();
            // Use centralized color service for consistent colors
            return getDisplayStatusColors(status).backgroundColor;
          })()
        }}
      >
        <h3 className="field-name" style={{
          color: (() => {
            const status = getActualMatchStatus();
            // Use black text for light green (no_data), white for all others
            return status === 'no_data' ? '#000000' : 'white';
          })()
        }}>
          {fieldName}
          {isOverridden && (
            <span 
              style={{
                marginLeft: '0.5rem',
                fontSize: '0.75rem',
                color: (() => {
                  const status = getActualMatchStatus();
                  return status === 'no_data' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)';
                })(),
                fontWeight: 'normal'
              }}
              title={`Overridden by ${data.override_created_by || 'User'}`}
            >
              (Overridden)
            </span>
          )}
        </h3>
        <div className="match-indicators">
          {onEdit && (
            <button
              className="edit-button"
              data-cy="edit-button"
              onClick={editingDisabled ? undefined : () => onEdit(fieldName, data)}
              disabled={editingDisabled}
              title={editingDisabled ? "Please save results to database before editing" : "Override field values"}
              style={{
                background: 'none',
                border: 'none',
                cursor: editingDisabled ? 'not-allowed' : 'pointer',
                padding: '0.25rem',
                borderRadius: '4px',
                color: (() => {
                  if (editingDisabled) {
                    return '#9ca3af'; // Gray when disabled
                  }
                  const status = getActualMatchStatus();
                  return status === 'no_data' ? '#000000' : 'white';
                })(),
                opacity: editingDisabled ? 0.5 : 1,
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => {
                if (editingDisabled) {
                  e.target.style.background = 'none';
                  e.target.style.color = '#9ca3af';
                  return;
                }
                const status = getActualMatchStatus();
                if (status === 'no_data') {
                  e.target.style.background = 'rgba(0, 0, 0, 0.05)';
                  e.target.style.color = '#374151';
                } else {
                  e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = '#93c5fd';
                }
              }}
              onMouseLeave={(e) => {
                if (editingDisabled) {
                  e.target.style.background = 'none';
                  e.target.style.color = '#9ca3af';
                  return;
                }
                const status = getActualMatchStatus();
                e.target.style.background = 'none';
                e.target.style.color = status === 'no_data' ? '#000000' : 'white';
              }}
            >
              <Edit3 size={16} />
            </button>
          )}
        </div>

      </div>

      {/* Main content area - will grow to fill space */}
      <div className="tile-content-wrapper">
        {/* Values */}
        <div className="tile-values">
          <div className="value-row label-only">
            <span className="value-label">Core</span>
          </div>
          <div className="value-row value-only">
            <span className="value-text master-value" data-cy="value-core">
              {formatValue(data?.master_value)}
            </span>
          </div>
          <div className="value-row label-only">
            <span className="value-label">Documents</span>
          </div>
          <div className="value-row value-only">
            <span className="value-text document-value" data-cy="value-documents">
              {formatValue(data?.value)}
            </span>
          </div>
        </div>

        {/* Comment */}
        {data?.comment && (
          <div className="tile-comment">
            <span className="comment-icon">ℹ</span>
            <span className="comment-text">{data.comment}</span>
          </div>
        )}
      </div>

      {/* Discrepancy Warning - Removed: header/footer colors already show match status */}

      {/* Metadata - moved to bottom with status-colored background */}
      <div 
        className="tile-metadata-bottom"
        style={{ 
          backgroundColor: (() => {
            const status = getActualMatchStatus();
            switch (status) {
              case 'perfect':
              case 'matched':
                return '#22c55e'; // Primary green
              case 'maybe':
                return '#f59e0b'; // Primary orange
              case 'mismatch':
                return '#ef4444'; // Primary red
              default:
                return '#6b7280'; // Primary gray
            }
          })()
        }}
      >
        <div className="metadata-content">
          {/* Status indicator */}
          <div className="status-indicator-container">
            {(() => {
              const status = getActualMatchStatus();
              const styles = getMatchStatusStyles(status);
              return (
                <div 
                  className="status-indicator-badge"
                  style={{ 
                    background: styles.background,
                    border: `2px solid ${styles.borderColor}`,
                    color: styles.color,
                    width: '31px', // 30% bigger than 24px
                    height: '31px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title={`Status: ${status}`}
                >
                  {getMatchStatusOverrideIcon(status, 16)} {/* 30% bigger than 12px */}
                </div>
              );
            })()}
          </div>
          
          {/* Metadata rows */}
          <div className="metadata-rows">
            <div className="metadata-row">
              <span className="metadata-label">Source:</span>
              <span className="metadata-value">
                {data?.source ? 
                  `${data.source.toUpperCase()}${data.page ? ` (${data.page})` : ''}` : 
                  'N/A'
                }
              </span>
            </div>
            {data?.document_date && (
              <div className="metadata-row">
                <span className="metadata-label">Document Date:</span>
                <span className="metadata-value">
                  {(() => {
                    try {
                      const date = new Date(data.document_date);
                      return date.toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                      });
                    } catch (error) {
                      return data.document_date; // Fallback to original if parsing fails
                    }
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsTile;
